import React, { useState, useEffect, useRef, useMemo } from "react";
import { X, ArrowLeft } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { entityKindMap } from "../../utils";
import EditableFactRenderer from "../../Dashboard/Components/EditableFactRenderer";
import ErrorRenderer from "../../Dashboard/Components/ErrorRenderer";
import {
  FactValue,
  EntityKind,
  Fact,
  SchemaFact,
  EntityCreationFact,
  ErrorEntitiesViolateRule,
  ErrorSchemaInvalid,
} from "../../Dashboard/types";
import { createEntity } from "../../Dashboard/api";
import { useRefreshRmsSearch } from "../../Search/useTableData";
import { createStore, useStore } from "zustand";
import type { StoreApi } from "zustand";
import { useEntityNavigate } from "../../Dashboard/entityNavigation";
import { missingRequiredFactNames } from "./createBlockers";

interface EntityFactInputProps {
  onClose: () => void;
  onBack: () => void;
  // Render the back arrow only when a genuine prior step exists (the type
  // chooser). A back arrow that only dismisses was "two dismissals with
  // different meanings" (#11426 component sweep, M1).
  showBack?: boolean;
  entityType: EntityKind;
  schemaFacts: Fact[];
  schemaDefinitions: SchemaFact[];
  onSuccess?: () => void;
  initialName?: string;
  preventNavigation?: boolean; // If true, don't navigate after creation
}

const EntityFactInput: React.FC<EntityFactInputProps> = ({
  onClose,
  onBack,
  showBack = false,
  entityType,
  schemaFacts,
  schemaDefinitions,
  onSuccess,
  initialName = "",
  preventNavigation = false,
}) => {
  const queryClient = useQueryClient();
  const refreshRmsSearch = useRefreshRmsSearch();
  const [entityName, setEntityName] = useState(initialName);
  const [error, setError] = useState<
    ErrorEntitiesViolateRule | ErrorSchemaInvalid | string | null
  >(null);
  const [creating, setCreating] = useState(false);
  const entityNavigate = useEntityNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // The draft lives in a store owned by this form instance (#12180). A form
  // opened from inside another form (a field's picker creating the record it
  // needs) used to share one module-level store with its parent: its values
  // leaked into the parent's create request, and its unmount cleared the
  // parent's draft while the parent's inputs still showed their values, so
  // every required field read as missing.
  const [store] = useState(() => createEntityCreationStore());
  const setFactFromValue = useStore(store, (s) => s.setFactFromValue);
  const getFacts = useStore(store, (s) => s.getFacts);
  const allFacts = useStore(store, (s) => s.facts);

  // Required fields still empty in the live draft. Drives which fields to
  // highlight after a blocked submit — deliberately NOT a button-disable: the
  // user should be able to click Create and be shown what's missing (#10597).
  const missingRequiredNames = useMemo(
    () => missingRequiredFactNames(schemaDefinitions, allFacts),
    [allFacts, schemaDefinitions]
  );
  const nameMissing = entityName.trim() === "";
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const factRowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Get entity info from entityKindMap
  const entityInfo = entityKindMap[entityType];

  // Update entity name when initialName changes
  useEffect(() => {
    if (initialName) {
      setEntityName(initialName);
    }
  }, [initialName]);

  // Scroll to top when error changes
  useEffect(() => {
    if (error && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [error]);

  // Memoize per-fact callbacks so they are stable across re-renders.
  // This prevents infinite loops: subscribing to store.facts for the Create
  // button causes re-renders, which must NOT create new onChange refs or
  // ScalarValueEditor's useEffect will fire and update the store again.
  const factCallbacks = useMemo(() => {
    const callbacks: Record<
      string,
      (isDirty: boolean, draftValue: FactValue | FactValue[]) => void
    > = {};
    schemaFacts.forEach((fact) => {
      callbacks[fact.name] = (
        isDirty: boolean,
        draftValue: FactValue | FactValue[]
      ) => {
        setFactFromValue(fact.name, isDirty, draftValue);
      };
    });
    return callbacks;
  }, [schemaFacts, setFactFromValue]);

  const handleSave = async () => {
    if (schemaFacts.length === 0) {
      setError("Schema not loaded");
      return;
    }

    // Surface (don't silently block on) missing required fields: mark the
    // attempt, then scroll to + highlight the first empty required field so
    // it's clear why creation can't proceed (#10597).
    setAttemptedSubmit(true);

    if (!entityName.trim()) {
      nameInputRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      nameInputRef.current?.focus();
      return;
    }

    const facts = getFacts();
    const missing = missingRequiredFactNames(schemaDefinitions, facts);
    if (missing.length > 0) {
      const row = factRowRefs.current[missing[0]];
      row?.scrollIntoView({ behavior: "smooth", block: "center" });
      row
        ?.querySelector<HTMLElement>("input, textarea, select, button")
        ?.focus();
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const createdEntity = await createEntity(
        queryClient,
        entityType,
        entityName.trim(),
        facts,
        schemaDefinitions
      );

      console.log("Entity created successfully:", createdEntity);

      // Clear visible rows on every per-view RMS store + drop tanstack-query
      // search caches so the new entity surfaces in the table.
      refreshRmsSearch();

      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }

      // Refresh the page or navigate to the new entity
      if (createdEntity?.id && !preventNavigation) {
        // Navigate to the new entity dashboard
        entityNavigate(createdEntity);
      } else {
        // Just close the modal if we don't have an ID or navigation is prevented
        onClose();
      }
    } catch (err: any) {
      console.error("Failed to create entity:", err);

      // Check if it's a structured error object with message and entities
      if (err && typeof err === "object" && "message" in err) {
        // This handles both ErrorEntitiesViolateRule and ErrorSchemaInvalid
        setError(err);
      } else if (err instanceof Error) {
        // Regular Error object
        setError(err.message);
      } else if (typeof err === "string") {
        // String error
        setError(err);
      } else {
        // Fallback to generic error message
        setError("Failed to create entity. Please try again.");
      }
    } finally {
      setCreating(false);
    }
  };

  const handleCancel = () => {
    // Check if there are any unsaved changes
    const facts = getFacts();
    const hasChanges =
      Object.keys(facts).length > 0 || entityName.trim() !== "";

    if (hasChanges) {
      const confirmed = window.confirm(
        "You have unsaved changes. Are you sure you want to cancel?"
      );
      if (!confirmed) return;
    }
    onClose();
  };

  return (
    <>
      <div className="flex justify-between items-center border-b px-6 py-4">
        <div className="flex items-center gap-4">
          {showBack && (
            <button
              onClick={onBack}
              aria-label="Back to record types"
              className="text-gray-400 hover:text-gray-600"
            >
              <ArrowLeft size={24} />
            </button>
          )}
          {/* No subtitle: the title already says it (#11426 component
              sweep, M1 — the copy rule). */}
          <h2 className="text-2xl font-semibold text-gray-900">
            Create New {entityInfo?.label || entityType}
          </h2>
        </div>
        <button
          onClick={handleCancel}
          className="text-gray-400 hover:text-gray-600"
        >
          <X size={24} />
        </button>
      </div>

      <div className="flex-1 overflow-auto p-6" ref={scrollContainerRef}>
        {error && (
          <div className="mb-4">
            <ErrorRenderer error={error} />
          </div>
        )}
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {entityInfo?.label || entityType} Name *
            </label>
            <input
              ref={nameInputRef}
              type="text"
              value={entityName}
              onChange={(e) => setEntityName(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                attemptedSubmit && nameMissing
                  ? "border-red-400 ring-1 ring-red-400 focus:ring-red-500"
                  : "border-gray-300 focus:ring-[var(--fp-ink-3)]"
              }`}
              placeholder={`Enter ${entityInfo?.singular || "entity"} name`}
              autoFocus
            />
          </div>

          <div className="border-t pt-6">
            {/* Band grammar, not a title-case heading (M1): the modal title
                already names the kind, so the section states only what the
                rows are. */}
            <h3
              className="font-semibold uppercase tracking-wider mb-4"
              style={{ color: "var(--fp-ink-3)", fontSize: "var(--fp-t-fine)" }}
            >
              Facts
            </h3>
            <div className="space-y-4">
              {schemaFacts.map((fact) => {
                const highlightMissing =
                  attemptedSubmit && missingRequiredNames.includes(fact.name);
                return (
                  <div
                    key={fact.name}
                    ref={(el) => {
                      factRowRefs.current[fact.name] = el;
                    }}
                    className={`rounded-md transition-shadow ${
                      highlightMissing
                        ? "ring-2 ring-red-400 ring-offset-2"
                        : ""
                    }`}
                  >
                    <EditableFactRenderer
                      definition={fact}
                      fact={fact}
                      onChange={factCallbacks[fact.name]}
                      showTypeMeta={false}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t px-6 py-4 flex justify-end space-x-3">
        <button
          onClick={handleCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={creating}
          className={`px-4 py-2 text-sm font-medium border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 flex items-center ${
            creating
              ? "text-gray-400 bg-gray-300 cursor-not-allowed"
              : "text-white bg-gray-800 hover:bg-gray-900"
          }`}
        >
          {creating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Creating...
            </>
          ) : (
            <>Create {entityInfo?.label || entityType}</>
          )}
        </button>
      </div>
    </>
  );
};

export default EntityFactInput;

interface EntityCreationState {
  facts: Record<string, EntityCreationFact | null>;

  setFact: (factName: string, value: EntityCreationFact | null) => void;

  // Helper to convert FactValue to EntityCreationFact
  setFactFromValue: (
    factName: string,
    isDirty: boolean,
    draftValue: FactValue | FactValue[]
  ) => void;

  getFacts: () => Record<string, EntityCreationFact>;
}

// One store per mounted form, never shared: a form nested inside another
// (a field's picker creating a record) must not see or clear its parent's
// draft (#12180).
export const createEntityCreationStore = (): StoreApi<EntityCreationState> =>
  createStore<EntityCreationState>((set, get) => ({
    facts: {},

    setFact: (factName, value) => {
      set((state) => {
        const newFacts = { ...state.facts };

        if (value === null) {
          delete newFacts[factName];
        } else {
          newFacts[factName] = value;
        }

        return { facts: newFacts };
      });
    },

    setFactFromValue: (factName, isDirty, draftValue) => {
      let factValue: EntityCreationFact | null = null;

      // For entity creation, we want to include values even if not dirty
      // (e.g., pre-filled default values that the user didn't change)
      // Check if the value is non-empty
      const hasValue = Array.isArray(draftValue)
        ? draftValue.length > 0 &&
          draftValue.some((v) => v.scalar !== null || v.entity !== null)
        : draftValue.scalar !== null || draftValue.entity !== null;

      if (isDirty || hasValue) {
        if (Array.isArray(draftValue)) {
          // Array fact
          factValue = {
            values: draftValue.map((v: FactValue) => ({
              scalar: v.scalar,
              entity: v.entity?.id || null,
            })),
          };
        } else {
          // Scalar fact
          factValue = {
            value: {
              scalar: draftValue.scalar,
              entity: draftValue.entity?.id || null,
            },
          };
        }
      }

      get().setFact(factName, factValue);
    },

    getFacts: () => {
      const state = get();
      // Filter out null values
      const cleanFacts: Record<string, EntityCreationFact> = {};
      Object.entries(state.facts).forEach(([key, value]) => {
        if (value !== null) {
          cleanFacts[key] = value;
        }
      });
      return cleanFacts;
    },
  }));
